import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useI18n } from '../contexts/I18nContext';
import { gameData } from '../data/levels';
import videoManager from '../utils/videoManager';

const ChaptersContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #F5F5DC 0%, #E8D5B7 100%);
  display: flex;
  position: relative;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grid" width="10" height="10" patternUnits="userSpaceOnUse"><path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(139, 69, 19, 0.03)" stroke-width="0.5"/></pattern></defs><rect width="100" height="100" fill="url(%23grid)"/></svg>');
    opacity: 0.3;
    z-index: 0;
  }
`;

const Sidebar = styled.aside`
  width: 240px;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-right: 1px solid rgba(139, 69, 19, 0.1);
  position: relative;
  z-index: 1;
  transition: transform 0.3s ease;
  display: flex;
  flex-direction: column;
  height: 100vh;

  @media (max-width: 1024px) {
    position: fixed;
    top: 0;
    left: 0;
    transform: translateX(-100%);
    z-index: 1000;
    height: 100vh;
  }

  @media (max-width: 768px) {
    width: 280px; /* 平板上增加宽度以容纳内容 */
  }

  @media (max-width: 480px) {
    width: 260px; /* 手机上适当调整 */
  }

  @media (max-width: 380px) {
    width: 240px; /* 小屏手机保持合适尺寸 */
  }

  &.open {
    transform: translateX(0);
  }
`;

const SidebarHeader = styled.div`
  padding: 1.2rem;
  border-bottom: 1px solid rgba(139, 69, 19, 0.1);
  background: linear-gradient(135deg, #8B4513, #A0522D);
  color: white;
  flex-shrink: 0;
`;

const SidebarTitle = styled.h2`
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0;
`;

const ChapterList = styled.div`
  padding: 0.5rem 0;
  overflow-y: auto;
  flex: 1;
  scrollbar-width: thin;
  scrollbar-color: rgba(139, 69, 19, 0.3) transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(139, 69, 19, 0.3);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(139, 69, 19, 0.5);
  }
`;

const ChapterNavItem = styled.div<{ isActive?: boolean }>`
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${props => props.isActive ? 'rgba(139, 69, 19, 0.08)' : 'transparent'};
  border-left: ${props => props.isActive ? '3px solid #8B4513' : '3px solid transparent'};

  &:hover {
    background: rgba(139, 69, 19, 0.05);
  }
`;

const ChapterNavLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const ChapterNavNumber = styled.div`
  font-size: 0.875rem;
  font-weight: 600;
  color: #8B4513;
`;

const ChapterNavName = styled.div`
  font-size: 0.75rem;
  color: #6B7280;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const ChapterNavRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #8B4513, #A0522D);
  color: white;
  border-radius: 50%;
  font-weight: 600;
  font-size: 0.75rem;
`;

const MainContent = styled.main`
  flex: 1;
  padding: 0;
  position: relative;
  z-index: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;

  @media (max-width: 1024px) {
    padding-top: 1rem; /* 为移动菜单按钮预留空间 */
  }

  @media (max-width: 768px) {
    padding-top: 1.5rem; /* 平板上增加顶部间距 */
  }

  @media (max-width: 480px) {
    padding-top: 1.5rem;
    padding-left: 0.5rem;
    padding-right: 0.5rem;
  }

  @media (max-width: 380px) {
    padding-top: 1.2rem;
    padding-left: 0.25rem;
    padding-right: 0.25rem;
  }
`;

const ChapterDetail = styled.div<{ isVisible?: boolean }>`
  display: ${props => props.isVisible ? 'flex' : 'none'};
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 1200px;
  padding: 2rem;
  animation: fadeIn 0.5s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }

  @media (max-width: 480px) {
    padding: 1rem 0.75rem;
  }

  @media (max-width: 380px) {
    padding: 0.75rem 0.5rem;
  }
`;

const ChapterTitle = styled.h2`
  font-size: 2rem;
  color: #2C3E50;
  margin-bottom: 1.5rem;
  font-weight: 700;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
`;

const VideoContainer = styled.div`
  width: 100%;
  max-width: 900px;
  aspect-ratio: 16/9;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 8px 24px rgba(139, 69, 19, 0.3);
  border: 4px solid rgba(139, 69, 19, 0.2);
  background: #000;
`;

const VideoPlayer = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  border-radius: 12px;
`;

interface VideoPlayerWrapperProps {
  src: string;
  title: string;
  isVisible: boolean;
  onVideoLoad?: (iframe: HTMLIFrameElement) => void;
}

const VideoPlayerWrapper: React.FC<VideoPlayerWrapperProps> = ({
  src,
  title,
  isVisible,
  onVideoLoad
}) => {
  const videoRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (isVisible && videoRef.current) {
      // 当视频变为可见时，将其设置为当前播放视频
      onVideoLoad?.(videoRef.current);
    }
  }, [isVisible, onVideoLoad]);

  if (!isVisible) {
    return null;
  }

  return (
    <VideoPlayer
      ref={videoRef}
      src={src}
      title={title}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
      allowFullScreen
    />
  );
};


const MobileMenuButton = styled.button<{ sidebarOpen?: boolean }>`
  display: none; /* 默认隐藏，只在移动端显示 */
  position: fixed;
  top: 80px; /* 调整位置，为header预留空间 */
  left: 1rem;
  z-index: 1001;
  background: linear-gradient(135deg, #8B4513, #A0522D);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(139, 69, 19, 0.3);
  transition: all 0.3s ease;
  font-size: 1rem;
  font-weight: 600;
  min-height: 44px; /* 确保触摸友好的最小尺寸 */
  min-width: 44px;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.sidebarOpen ? 0 : 1};
  visibility: ${props => props.sidebarOpen ? 'hidden' : 'visible'};
  transform: ${props => props.sidebarOpen ? 'translateX(-100%)' : 'translateX(0)'};

  &:hover {
    transform: ${props => props.sidebarOpen ? 'translateX(-100%)' : 'translateY(-2px)'};
    box-shadow: 0 6px 16px rgba(139, 69, 19, 0.4);
  }

  &:active {
    transform: ${props => props.sidebarOpen ? 'translateX(-100%)' : 'translateY(-1px)'};
    box-shadow: 0 4px 12px rgba(139, 69, 19, 0.3);
  }

  @media (max-width: 1024px) {
    display: flex;
  }

  @media (max-width: 768px) {
    top: 70px; /* 平板设备稍微调整 */
    padding: 0.6rem 0.8rem;
    font-size: 0.9rem;
  }

  @media (max-width: 480px) {
    top: 60px; /* 小屏手机进一步调整 */
    padding: 0.5rem 0.7rem;
    font-size: 0.85rem;
    border-radius: 6px;
  }

  @media (max-width: 380px) {
    top: 55px; /* 超小屏设备 */
    padding: 0.5rem;
    font-size: 0.8rem;
    min-width: 40px;
    min-height: 40px;
  }
`;

const Overlay = styled.div<{ isVisible?: boolean }>`
  display: ${props => props.isVisible ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;

  @media (min-width: 1024px) {
    display: none;
  }
`;

const ChapterSubtitles = [
  'Prelude',
  'The Forest',
  'The Caverns',
  'The Temple',
  'The Ocean',
  'The Workshop',
  'The Maze',
  'The Journey',
  'Beyond'
];

const getYouTubeEmbedUrl = (url: string) => {
  if (!url) return '';
  const videoId = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/)?.[1];
  return videoId ? `https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${window.location.origin}` : '';
};

const Chapters: React.FC = () => {
  const { t } = useI18n();
  const [selectedChapter, setSelectedChapter] = useState(gameData[0].id);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleChapterSelect = (chapterId: string) => {
    // 暂停当前播放的视频
    videoManager.pauseCurrentVideo();

    setSelectedChapter(chapterId);
    setSidebarOpen(false);
  };

  const handleVideoLoad = (iframe: HTMLIFrameElement) => {
    // 设置新的视频为当前播放视频
    videoManager.setCurrentVideo(iframe);
  };

  
  return (
    <>
      <MobileMenuButton
        sidebarOpen={sidebarOpen}
        onClick={() => setSidebarOpen(true)}
      >
        ☰
      </MobileMenuButton>

      <Overlay isVisible={sidebarOpen} onClick={() => setSidebarOpen(false)} />

      <ChaptersContainer>
        <Sidebar className={sidebarOpen ? 'open' : ''}>
          <SidebarHeader>
            <SidebarTitle>{t('chapters.title')}</SidebarTitle>
          </SidebarHeader>

          <ChapterList>
            {gameData.map((chapter) => {
              const isActive = selectedChapter === chapter.id;

              return (
                <ChapterNavItem
                  key={chapter.id}
                  isActive={isActive}
                  onClick={() => handleChapterSelect(chapter.id)}
                >
                  <ChapterNavLeft>
                    <ChapterNavNumber>Chapter {chapter.number}</ChapterNavNumber>
                    <ChapterNavName>{ChapterSubtitles[chapter.number - 1]}</ChapterNavName>
                  </ChapterNavLeft>
                  <ChapterNavRight>{chapter.number}</ChapterNavRight>
                </ChapterNavItem>
              );
            })}
          </ChapterList>
        </Sidebar>

        <MainContent>
          {gameData.map((chapter) => (
            <ChapterDetail
              key={chapter.id}
              isVisible={selectedChapter === chapter.id}
            >
              <ChapterTitle>◈ {chapter.name}</ChapterTitle>
              <VideoContainer>
                {chapter.videoUrl ? (
                  <VideoPlayerWrapper
                    src={getYouTubeEmbedUrl(chapter.videoUrl)}
                    title={`${chapter.name} - Monument Valley 3 Walkthrough`}
                    isVisible={selectedChapter === chapter.id}
                    onVideoLoad={handleVideoLoad}
                  />
                ) : (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: 'white',
                    fontSize: '1.2rem'
                  }}>
                    Video Coming Soon
                  </div>
                )}
              </VideoContainer>
            </ChapterDetail>
          ))}
        </MainContent>
      </ChaptersContainer>
    </>
  );
};

export default Chapters;